// src/app/store.ts
import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage
import messagesReducer from './messagesSlice';
import contactsReducer from './contactSlice';
import authReducer from './authSlice';
import chatListReducer from './chatListSlice';
import userReducer from './userSlice';
import giftBrandsReducer from './giftBrandsSlice';
import nftReducer from './nftSlice';
import giftBrandShoppingCartReducer from './giftShoppingCartSlice';
import walletReducer from './walletSlice';
import uiReducer from './uiSlice';
import donationsReducer from './donationsSlice';
import bankReducer from './bankSlice';
import nftShoppingCartReducer from './nftShoppingCartSlice';
import depositsReducer from './depositsSlice';
import moveUsdReducer from './moveUsdSlice';
import countriesReducer from './countriesSlice';
import notificationsReducer from './notificationSlice';
import ndeipiCoinReducer from './ndeipiCoinSlice';
import postsReducer from './postSlice';
import rewardsReducer from './rewardsSlice';
import assetsReducer from './assetSlice';
import merchantPaymentsReducer from './merchantPaymentsSlice';
import tokensReducer from './tokenSlice'; // Import the token slice

// Persist config: Define which slices to persist
// const persistConfig = {
//   key: 'root',
//   storage,
//   whitelist: ['auth', 'user', 'wallet', 'ui'], // Only these reducers will be persisted
//   blacklist: ['wallet.signer'], // Explicitly exclude non-serializable data
// };


const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['token'], // Persist only the token
};

const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['user', 'receiversPublicKey'], // Persist critical user data
};

const contactsPersistConfig = {
  key: 'contacts',
  storage,
  whitelist: ['contacts'], // Persist only the list of contacts
};

const uiPersistConfig = {
  key: 'ui',
  storage,
  whitelist: ['profileSection', 'walletSection', 'selectedChatView', 'currentNavigationView','selectedMarketplaceTab','selectedWalletTab','selectedSettingsTab','selectedDenomination'], // Persist relevant UI state
};

// const messagesPersistConfig = {
//   key: 'messages',
//   storage,
//   whitelist: ['messages'], // Persist only the list of messages
// };
const chatListPersistConfig = {
  key: 'chatList',
  storage,
  whitelist: ['chatList','selectedContact','receiversPublicKey'], // Persist only the list of chat
};

const shoppingCartPersistConfig = {
  key: 'shoppingCart',
  storage,
  whitelist: ['items'], // Persist only the items in the shopping cart
};
const nftPersistConfig = {
  key: 'nft',
  storage,
  whitelist: ['collections'], // Persist relevant NFT state
};

// const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
// const persistedUserReducer = persistReducer(userPersistConfig, userReducer);




// Wrap individual reducers (or combine them first)
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedUserReducer = persistReducer(userPersistConfig, userReducer);
const persistedContactsReducer = persistReducer(contactsPersistConfig, contactsReducer);
// const persistedMessagesReducer = persistReducer(messagesPersistConfig, messagesReducer);
const persistedUIReducer = persistReducer(uiPersistConfig, uiReducer);
const persistedChatListReducer = persistReducer(chatListPersistConfig, chatListReducer);
const persistedGiftBrandShoppingCartReducer = persistReducer(shoppingCartPersistConfig, giftBrandShoppingCartReducer);
const persistedNftReducer = persistReducer(nftPersistConfig, nftReducer);
// const persistedWalletReducer = persistReducer(persistConfig, walletReducer);
// const persistedUIReducer = persistReducer(persistConfig, uiReducer);

export const store = configureStore({
  reducer: {
    messages: messagesReducer,//persistedMessagesReducer,
    contacts: persistedContactsReducer,
    auth: persistedAuthReducer, // Persisted
    chatList: persistedChatListReducer,
    user: persistedUserReducer, // Persisted
    giftBrands: giftBrandsReducer,
    nft: persistedNftReducer,
    shoppingCart: persistedGiftBrandShoppingCartReducer,
    wallet: walletReducer, // Persisted (except signer)
    ui: persistedUIReducer,
    donations: donationsReducer,
    bank: bankReducer,
    nftShoppingCart: nftShoppingCartReducer,
    deposits: depositsReducer,
    moveUsd: moveUsdReducer,
    countries: countriesReducer,
    notifications: notificationsReducer,
    ndeipiCoin: ndeipiCoinReducer,
    posts: postsReducer,
    rewards: rewardsReducer,
    assets: assetsReducer,
    merchantPayments: merchantPaymentsReducer,
    tokens: tokensReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, 'token/initialize/fulfilled'],
        ignoredPaths: ['wallet.signer'],
        serializableCheck: false,
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store); // Create persistor

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;